import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Linkedin, Instagram } from "lucide-react";
import { backend } from "@/integrations/firebase/client";
import csgLogo from "@/assets/csg-logo.jpg";
import { useLanguage } from "@/hooks/useLanguage";
import { navPath } from "@/lib/localizedPaths";
import { PlainProtected } from "@/components/ProtectedContact";
import { CONTACT, decodeContact } from "@/lib/contactProtect";

// Telefon ve e-posta kodlanmış tutulur; bkz. lib/contactProtect.ts.
// Böylece prerender edilmiş HTML ve JS bundle içinde düz metin aranarak bulunamaz.
const defaults: Record<string, string> = {
  istanbul_address: "Tomtom Mah. İstiklal Cad. No:189 Kat:2 D:3, Beyoğlu, Istanbul",
  istanbul_phone: "",
  istanbul_email: "",
  budapest_address: "Föld utca 53, 1032 Budapest, Hungary",
  budapest_phone: "",
  budapest_email: "",
  linkedin_url: "https://linkedin.com",
  instagram_url: "https://instagram.com",
  footer_text_en: "Transforming vision into experience since 1989. Premium event management and technical production across 15+ countries.",
  footer_text_tr: "1989'dan beri vizyonu deneyime dönüştürüyoruz. 15+ ülkede premium etkinlik yönetimi ve teknik prodüksiyon.",
  footer_text_de: "Wir verwandeln Visionen seit 1989 in Erlebnisse. Premium-Eventmanagement und technische Produktion in über 15 Ländern.",
};

export default function Footer() {
  const navigate = useNavigate();
  const { lang, t } = useLanguage();
  const [settings, setSettings] = useState<Record<string, string>>(defaults);

  useEffect(() => {
    // Kodlanmış iletişim sabitleri yalnızca tarayıcıda çözülür.
    const decoded: Record<string, string> = {
      istanbul_phone: decodeContact(CONTACT.istanbulPhone),
      istanbul_email: decodeContact(CONTACT.istanbulEmail),
      budapest_phone: decodeContact(CONTACT.budapestPhone),
      budapest_email: decodeContact(CONTACT.budapestEmail),
    };
    setSettings({ ...defaults, ...decoded });

    backend.from("company_settings").select("*").then(({ data }) => {
      if (data) {
        const s = { ...defaults, ...decoded };
        data.forEach(item => { if (item.value) s[item.key] = item.value; });
        setSettings(s);
      }
    });
  }, []);

  const handleAdminClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    const { data: { session } } = await backend.auth.getSession();
    navigate(session?.user ? "/admin" : "/admin/login");
  };

  const footerText = lang === "tr"
    ? (settings.footer_text_tr || defaults.footer_text_tr)
    : lang === "de"
    ? (settings.footer_text_de || defaults.footer_text_de || settings.footer_text_en || defaults.footer_text_en)
    : (settings.footer_text || settings.footer_text_en || defaults.footer_text_en);

  // Menü linkleri aktif dilin kanonik adreslerine gider (bkz. lib/localizedPaths.ts).
  const navItems = [
    { label: t("nav.about"), path: navPath("/about", lang) },
    { label: t("nav.services"), path: navPath("/services", lang) },
    { label: t("nav.portfolio"), path: navPath("/portfolio", lang) },
    { label: t("nav.news"), path: navPath("/news", lang) },
    { label: t("nav.contact"), path: navPath("/contact", lang) },
  ];

  return (
    <footer className="bg-card border-t border-border">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <img src={csgLogo} alt="CSG The Event Company" width={121} height={62} loading="lazy" decoding="async" className="h-[62px] w-auto" />
            <p className="mt-4 text-muted-foreground text-sm leading-relaxed">{footerText}</p>
          </div>
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-widest text-gold mb-6">{t("footer.navigation")}</h2>
            <div className="flex flex-col gap-3">
              {navItems.map((item) => (
                <Link key={item.path} to={item.path} className="text-sm text-muted-foreground hover:text-foreground transition-colors">{item.label}</Link>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-widest text-gold mb-6">Istanbul</h2>
            <div className="flex flex-col gap-3 text-sm text-muted-foreground">
              <div className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-electric" /><span>{settings.istanbul_address}</span></div>
              <div className="flex gap-2 items-center"><Phone className="w-4 h-4 shrink-0 text-electric" /><PlainProtected value={settings.istanbul_phone} kind="phone" /></div>
              <div className="flex gap-2 items-center"><Mail className="w-4 h-4 shrink-0 text-electric" /><PlainProtected value={settings.istanbul_email} kind="email" /></div>
            </div>
          </div>
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-widest text-gold mb-6">Budapest</h2>
            <div className="flex flex-col gap-3 text-sm text-muted-foreground">
              <div className="flex gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-electric" /><span>{settings.budapest_address}</span></div>
              <div className="flex gap-2 items-center"><Phone className="w-4 h-4 shrink-0 text-electric" /><PlainProtected value={settings.budapest_phone} kind="phone" /></div>
              <div className="flex gap-2 items-center"><Mail className="w-4 h-4 shrink-0 text-electric" /><PlainProtected value={settings.budapest_email} kind="email" /></div>
            </div>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="text-sm text-muted-foreground">
            <a href="#" onClick={handleAdminClick} className="hover:text-gold transition-colors">©</a> 2002-{new Date().getFullYear()} CSG The Event Company. {t("footer.rights")}
            <span className="mx-2 text-border">|</span>
            <Link to={navPath("/privacy", lang)} className="hover:text-gold transition-colors">{t("footer.privacy")}</Link>
          </span>
          <div className="flex items-center gap-4">
            <a href={settings.linkedin_url} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-muted-foreground hover:text-gold transition-colors"><Linkedin className="w-5 h-5" aria-hidden="true" /></a>
            <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-muted-foreground hover:text-gold transition-colors"><Instagram className="w-5 h-5" aria-hidden="true" /></a>
          </div>
        </div>
      </div>
    </footer>
  );
}
